import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { RequirementType } from '@/utils/membership/types'
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import type { CategoryRequirement } from '../types'

const formatRequirementType = (type: RequirementType) => {
  const label = type.toLowerCase().replace(/_/g, ' ')
  return label.charAt(0).toUpperCase() + label.slice(1)
}

export const createColumns = (
  onEdit: (requirement: CategoryRequirement) => void,
  onDelete: (requirement: CategoryRequirement) => void,
): ColumnConfig<CategoryRequirement>[] => [
  {
    key: 'label',
    header: 'Label',
    sortable: true,
    filterable: true,
    cell: (value) => <div className="font-medium">{value as string}</div>,
  },
  {
    key: 'type',
    header: 'Type',
    sortable: true,
    filterable: true,
    cell: (_value, row) => (
      <Badge variant="outline">{formatRequirementType(row.type)}</Badge>
    ),
  },
  {
    key: 'mandatory',
    header: 'Mandatory',
    sortable: true,
    filterable: true,
    cell: (_value, row) => (
      <Badge variant={row.mandatory ? 'destructive' : 'secondary'}>
        {row.mandatory ? 'Required' : 'Optional'}
      </Badge>
    ),
  },
  {
    key: 'description',
    header: 'Helper Text',
    sortable: false,
    filterable: true,
    cell: (value) => (
      <div className="max-w-[320px] truncate text-muted-foreground">
        {(value as string) || '—'}
      </div>
    ),
  },
  {
    key: 'actions',
    header: 'Actions',
    sortable: false,
    filterable: false,
    accessor: () => '',
    cell: (_value, row) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onClick={(event) => event.stopPropagation()}
        >
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation()
              onEdit(row)
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(event) => {
              event.stopPropagation()
              onDelete(row)
            }}
            className="text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
]
